import { useState } from "react";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import type { CreditNote, Invoice } from "../../services/financialService";

/**
 * Invoice, amount and reason for a credit note. The reason is written with
 * the assistant beside it, which reads the invoice chosen here -- so the three
 * are kept in state together. Mount it with a `key` per note so an edit
 * starts from that note's own values.
 */
export function CreditNoteFields({ editing, invoices }: { editing?: CreditNote; invoices: Invoice[] }) {
  const [invoice, setInvoice] = useState(editing ? String(editing.invoice) : "");
  const [amount, setAmount] = useState(editing?.amount ?? "");
  const [reason, setReason] = useState(editing?.reason ?? "");

  return (
    <>
      <Field label="Invoice" className="sm:col-span-2" hint="Only invoices with a balance still owed can be credited.">
        <select required name="invoice" className="input mt-1" value={invoice} onChange={(event) => setInvoice(event.target.value)}>
          <option value="">Choose an outstanding invoice</option>
          {editing && !invoices.some((entry) => entry.id === editing.invoice) && (
            <option value={editing.invoice}>{editing.invoice_number} · {editing.student_name}</option>
          )}
          {invoices.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.invoice_number} · {entry.student_name} · UGX {Number(entry.balance_due).toLocaleString()} due
            </option>
          ))}
        </select>
      </Field>
      <Field label="Credit amount (UGX)">
        <input
          required
          name="amount"
          type="number"
          min="1"
          step="0.01"
          className="input mt-1 tabular-nums"
          placeholder="0"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </Field>
      <Field as="div" label="Reason" className="sm:col-span-2 lg:col-span-3" hint="Kept for audit and shown to the reviewer and approver.">
        <AIAssistField
          name="reason"
          rows={2}
          value={reason}
          onChange={(next) => setReason(next)}
          placeholder="e.g. Billed twice for Term 3 transport"
          context={{
            key: "financial.credit_note_reason",
            params: { invoice: invoice || null, amount: amount || null, credit_note: editing?.id ?? null },
          }}
          draftLabel="Draft the reason"
          draftHint={invoice ? "Reads the chosen invoice. Jot why in a few words first, and it writes it up." : "Choose the invoice first, then draft."}
        />
      </Field>
    </>
  );
}
